import {
  getAccHostname,
  getAdminApiUrl
} from '@adsk/acs-sc-web-platform-api';
import { PRODUCT_IDS } from '@adsk/alloy-provider';
import {
  SERVICE_ACCESSES,
  SERVICES,
  COMPONENT_VARIANTS,
  TIME_ZONES,
  getConstructionApiUrl,
  formatDate,
} from '@adsk/acs-admin-ui-components-helpers';
import {
  TEMPLATE_PROJECT_TYPE,
  TEMPLATE_PROJECT_CLASSIFICATION,
  ACS_UNIFIED_SOURCE,
  ACTIVE_SERVICES,
} from './constants';

export const isDuplicateNameError = (errors) => {
  const re = /^name:.+\sunique validation failed/i;

  return errors.some(
    (error) => error.field === 'name' && re.test(error.detail)
  );
};

export const getUserActiveIngServices = (services) =>
  services
    .filter(({ status }) => ACTIVE_SERVICES.includes(status))
    .map(({ serviceName }) => ({
      serviceName,
      access:
        serviceName === SERVICES.PROJECT_ADMIN
          ? SERVICE_ACCESSES.ADMIN
          : SERVICE_ACCESSES.NONE,
    }));

export const buildAccountServicesRequest = (accountId) =>
  `${getAdminApiUrl()}/v1/accounts/${accountId}?fields=services`;

export const buildAccountsRequest = () => {
  return `${getAdminApiUrl()}/v1/accounts?filter[accessLevels]=accountAdmin,projectAdmin&fields=name,accessLevels,hasAcsUnifiedProjects,isAccEligible,createProjectAccessLevels&limit=200`;
};

export const buildTemplatesRequest = (accountId, useConstructionApi) => {
  const url = useConstructionApi ? getConstructionApiUrl() : getAdminApiUrl();
  const filters = useConstructionApi
    ? `&filter[classification]=${TEMPLATE_PROJECT_CLASSIFICATION}`
    : `&filter[source]=${ACS_UNIFIED_SOURCE}`;

  return `${url}/v1/accounts/${accountId}/projects?filter[type]=${TEMPLATE_PROJECT_TYPE}${filters}&filter[status]=active&fields=name&limit=200`;
};

export const buildCreateProjectRequest = ({
  accountId,
  projectData,
  useConstructionApi = false,
}) => {
  const url = useConstructionApi ? getConstructionApiUrl() : getAdminApiUrl();

  return [
    `${url}/v1/accounts/${accountId}/projects`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(projectData),
    },
  ];
};

export const buildAddUserToProjectRequest = ({
  projectId,
  userData,
  useConstructionApi = false,
}) => {
  const url = useConstructionApi ? getConstructionApiUrl() : getAdminApiUrl();

  return [
    `${url}/v1/projects/${projectId}/users`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    },
  ];
};

export const getProjectAdminConsoleTeamPageUrl = ({
  isEmsAccount,
  projectId,
  uiDomainUrl,
}) => {
  if (isEmsAccount && uiDomainUrl) {
    return `${uiDomainUrl}/project-admin/members/projects/${projectId}?openCurrentUserProfilePanel=true`;
  }

  return `${getAccHostname()}/project-admin/members/projects/${projectId}?openCurrentUserProfilePanel=true`;
}

export const buildGetProjectUserRequest = (
  projectId,
  userId,
  useConstructionApi
) => {
  const baseUrl = useConstructionApi
    ? getConstructionApiUrl()
    : getAdminApiUrl();

  return `${baseUrl}/v1/projects/${projectId}/users/${userId}`;
};

const checkTimeZoneChanged = (selectedTimeZone) => {
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return currentTimeZone && TIME_ZONES.includes(currentTimeZone)
    ? selectedTimeZone?.value !== currentTimeZone
    : selectedTimeZone !== null;
};

export const dataEntered = (
  initialState,
  state,
  accountsCount,
  additionalFieldsActivated = false,
  variant = COMPONENT_VARIANTS.PROJECT,
  accountId = undefined,
  templateId = undefined
) =>
  (variant === COMPONENT_VARIANTS.PROJECT &&
    !accountId &&
    state.selectedAccount !== null &&
    accountsCount > 1) ||
  state.projectName !== initialState.projectName ||
  state.selectedProjectType !== initialState.selectedProjectType ||
  state.autocompletedAddress !== initialState.autocompletedAddress ||
  state.address.line1 !== initialState.address.line1 ||
  state.address.line2 !== initialState.address.line2 ||
  state.address.city !== initialState.address.city ||
  state.address.stateOrProvince !== initialState.address.stateOrProvince ||
  state.address.zip !== initialState.address.zip ||
  state.address.country !== initialState.address.country ||
  (state.selectedTemplate !== initialState.selectedTemplate && !templateId) ||
  state.startDate !== initialState.startDate ||
  state.endDate !== initialState.endDate ||
  (additionalFieldsActivated &&
    (state.jobNumber !== initialState.jobNumber ||
      checkTimeZoneChanged(state.selectedTimeZone) ||
      // state.selectedPhase !== initialState.selectedPhase ||
      state.projectValue !== initialState.projectValue ||
      state.selectedCurrency?.value !== initialState.selectedCurrency.value));

export const validProjectValue = (value) => {
  const regInteger = /^\d+$/;

  return value !== '' ? regInteger.test(value) : true;
};

export const validateDate = (data) => {
  const date = data && data.date ? data.date : null;

  return date;
};

export const getProjectData = (
  state,
  additionalFieldsActivated,
  useConstructionApi
) => {
  const {
    address,
    endDate,
    jobNumber,
    projectName,
    projectValue,
    selectedCurrency,
    selectedProjectType,
    selectedTemplate,
    selectedTimeZone,
    startDate,
  } = state;
  const servicesOrDefaultEmptyProducts = useConstructionApi
    ? {}
    : {
        services: Object.values(SERVICES).map((serviceName) => ({
          serviceName,
        })),
      };

  return {
    name: projectName,
    type: selectedProjectType?.value || '',
    ...(!useConstructionApi && { isAcsUnified: true }),
    ...(startDate && {
      startDate: formatDate(startDate),
    }),
    ...(endDate && { endDate: formatDate(endDate) }),
    ...(address.addressLine1 && { addressLine1: address.addressLine1 }),
    ...(address.addressLine2 && { addressLine2: address.addressLine2 }),
    ...(address.city && { city: address.city }),
    ...(address.stateOrProvince && {
      stateOrProvince: address.stateOrProvince,
    }),
    ...(address.postalCode && { postalCode: address.postalCode }),
    ...(address.country && { country: address.country }),
    ...(selectedTemplate
      ? { template: { projectId: selectedTemplate.id } }
      : servicesOrDefaultEmptyProducts),
    ...(additionalFieldsActivated && {
      ...(jobNumber && { jobNumber }),
      ...(selectedTimeZone && { timezone: selectedTimeZone.value }),
      // ...(selectedPhase && { currentPhase: selectedPhase.value }),
      ...(projectValue && {
        projectValue: {
          value: parseFloat(projectValue),
          currency: selectedCurrency.value,
        },
      }),
    }),
  };
};

export const getAdminUserAccess = (useConstructionApi, services) => {
  if (useConstructionApi) {
    return [
      {
        key: PRODUCT_IDS.PROJECT_ADMINISTRATION,
        access: SERVICE_ACCESSES.ADMIN,
      },
    ];
  }

  return getUserActiveIngServices(services);
};
